import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Button, Form} from "react-bootstrap";
import PageTitle from "../components/common/PageTitle";
import api from '../api/api'
import { useTranslation } from "react-i18next";
import Html5QrcodePlugin from "./Html5QrcodePlugin";
import {  useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, TextField } from '@mui/material';
import '../css/SensorAdminPages.css';
import { getSensorDepths } from '../utils/sensorDepths';

const EditSensorByAdmin = () => {
 

  const params = useParams()
  let id = params.id
  const { t, i18n } = useTranslation();

  const navigate = useNavigate()
  const location = useLocation();

  const { lastDataTime, formattedTime, sensorState } = location.state || {};
  const [settingValues, setSettingValues] = useState([])
  const [frequency, setFrequency] = useState('')
  const [sendFrequency, setSendFrequency] = useState('')
  const [firmwareVersion, setFirmwareVersion] = useState('1')
  const [initialFirmwareVersion, setInitialFirmwareVersion] = useState('1')
  const [dateSetting, setDateSetting] = useState([])
  const [minSetting, setMinSetting] = useState({})
  const [sensorName, setSensorName] = useState("");
  const [sensorDepths, setSensorDepths] = useState({ mv1: '20', mv2: '40', mv3: '60' });
  const [configurationHistory, setConfigurationHistory] = useState([]);
  const [historyFilters, setHistoryFilters] = useState({ level: '', from: '', to: '' });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [maxSetting, setMaxSetting] = useState({})
  const [simSettings, setSimSettings] = useState({
    simNumber: "",
    simIdentify: ""
  })
  const [formValid, setFormValid] = useState(false);
  const [showScan, setShowScan] = useState(false)
  const [resultScan, setResultScan] = useState('')
  const [extranelToken, setExtranelToken] = useState('')
  const [sensorCoords, setSensorCoords] = useState({
    lat: '',
    lon: '',
    locationInfo: {}
  })
  const onNewScanResult = (decodedText, decodedResult) => {
    setResultScan(decodedText)

    // Handle the result here.
  }

  const getSingleSensor = () => {
    api.get(`/admin/single-sensor/${id}`,{timeout:0})
      .then(res => {
        let SensorData = res.data.sensor
        let SensorLocation = res.data.location
        let userSupplier = res.data.user
        setResultScan(SensorData.code)
        setSensorCoords({ lat: SensorData.Latitude, lon: SensorData.Longitude, locationInfo: SensorLocation })
        setSensorName(SensorData.sensorName || "");
        const loadedFirmwareVersion = ['1', '2'].includes(String(SensorData.firmware_version))
          ? String(SensorData.firmware_version)
          : SensorData.firmware_profile === 'buffered_batch_v2' ? '2' : '1';
        setFirmwareVersion(loadedFirmwareVersion);
        setInitialFirmwareVersion(loadedFirmwareVersion);
        const depths = getSensorDepths(SensorData);
        setSensorDepths({
          mv1: String(depths.mv1),
          mv2: String(depths.mv2),
          mv3: String(depths.mv3),
        });
        let dataMapping = SensorData.dataMapping
        if (dataMapping.length > 0) {
          setSettingValues(dataMapping)
          dataMapping.map(data => {
            let frequency = data.frequency
            setFrequency(frequency)
            setSimSettings({ simIdentify: data.simIdentify, simNumber: data.simNumber })
            setDateSetting(data.date)
            setMinSetting(data.min)
            setMaxSetting(data.max)

          })
        }


        if (userSupplier != null) {
          setExtranelToken(userSupplier.external_api_token)
        }

        // La configuration CP est la source de verite des deux frequences.
        if (res.data.config?.frequence != null) {
          setFrequency(String(res.data.config.frequence))
        }
        if (res.data.config?.send_frequency != null) {
          setSendFrequency(String(res.data.config.send_frequency))
        }
      }).catch(error => {
        console.error(error)
        swal({
          title: t("Error"),
          icon: "error",
        });
      })
  }

  const loadConfigurationHistory = (page = historyPage) => {
    setIsHistoryLoading(true);
    api.get(`/admin/single-sensor/${id}/configuration-history`, {
      params: {
        page,
        pageSize: 25,
        level: historyFilters.level || undefined,
        from: historyFilters.from || undefined,
        to: historyFilters.to || undefined,
      },
    })
      .then((res) => {
        setConfigurationHistory(res.data.rows || []);
        setHistoryPagination(res.data.pagination || { page: 1, pageSize: 25, total: 0, totalPages: 1 });
      })
      .catch(() => {
        setConfigurationHistory([]);
        setHistoryPagination({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
        swal({ icon: 'error', title: t('Oops...'), text: t('config_history_load_error') });
      })
      .finally(() => setIsHistoryLoading(false));
  }


  useEffect(() => {
    getSingleSensor()
  }, [])

  useEffect(() => {
    loadConfigurationHistory();
  }, [id, historyPage, historyFilters]);

  const validateForm = () => {
    // Les frequences sont en secondes et doivent etre acceptees par l'Arduino.
    const isCodeValid = resultScan.trim() !== "";
    const readSeconds = Number(frequency);
    const sendSeconds = Number(sendFrequency);
    const isFrequencyValid = Number.isInteger(readSeconds) &&
      readSeconds >= 60 && readSeconds <= 86400;
    const isSendFrequencyValid = Number.isInteger(sendSeconds) &&
      sendSeconds >= 60 && sendSeconds <= 259200;
    const isFrequencyPairValid = isFrequencyValid && isSendFrequencyValid &&
      readSeconds <= sendSeconds;
    const isFirmwareVersionValid = ['1', '2'].includes(firmwareVersion);
    const areDepthsValid = Object.values(sensorDepths).every((value) => {
      const depth = Number(value);
      return Number.isInteger(depth) && depth >= 0 && depth <= 1000;
    }) && Number(sensorDepths.mv1) < Number(sensorDepths.mv2)
      && Number(sensorDepths.mv2) < Number(sensorDepths.mv3);
    const isCalibrationValid = ['Mv1', 'Mv2', 'Mv3'].every((level) => {
      const date = dateSetting?.[`${level}_date`];
      const minimum = minSetting?.[`${level}_min`];
      const maximum = maxSetting?.[`${level}_max`];
      const values = [date, minimum, maximum];
      const isEmpty = values.every((value) => value === undefined || value === null || value === '');
      if (isEmpty) return true;
      return Boolean(date)
        && Number.isFinite(Number(minimum))
        && Number.isFinite(Number(maximum))
        && Number(minimum) > Number(maximum);
    });

    setFormValid(
      isCodeValid && isFrequencyPairValid && isFirmwareVersionValid && areDepthsValid && isCalibrationValid
    );
  };

  useEffect(() => {
    validateForm();
  }, [
    resultScan,
    frequency,
    sendFrequency,
    dateSetting,
    minSetting,
    maxSetting,
    sensorDepths,
    firmwareVersion,
  ]);


  const editSensor = () => {
    let data = {
      sensor_id: id,
      sensorName: sensorName,
      code: resultScan,
      dataMapping: settingValues,
      frequency: frequency,
      sendFrequency: sendFrequency,
      simNumber: simSettings.simNumber,
      simIdentify: simSettings.simIdentify,
      lat: sensorCoords.lat,
      lon: sensorCoords.lon,
      mv1: sensorDepths.mv1,
      mv2: sensorDepths.mv2,
      mv3: sensorDepths.mv3,
      firmwareVersion
    }
    if (formValid) {
      api.post('/admin/edit-mapping', data)
        .then(response => {
          if (response.data.type === 'success') {
            swal(`${t('sensor_added')}`, {
              icon: "success",
            })
            getSingleSensor()
            if (historyPage === 1) loadConfigurationHistory(1);
            else setHistoryPage(1);
          }
        }).catch(err => {
          swal({
            icon: 'error',
            title: t('Oops...'),
            text: t(err.response?.data?.message, {
              defaultValue: err.response?.data?.message || t('Error'),
            })
          })
        })

    } else {
      swal({
        icon: "error",
        title: t("Error"),
        text: t('validate_sensor_config_error'),
      });
    }
  }

  const confirmSensorSave = () => {
    if (!formValid) {
      swal({
        icon: "error",
        title: t("Error"),
        text: t('validate_sensor_config_error'),
      });
      return;
    }

    swal({
      title: t('confirm_save_changes_title'),
      text: firmwareVersion !== initialFirmwareVersion
        ? t('sensor_firmware_change_warning')
        : t('confirm_save_changes_text'),
      icon: "warning",
      buttons: {
        cancel: {
          text: t('cancel'),
          visible: true,
        },
        confirm: {
          text: t('confirm'),
          value: true,
        },
      },
    }).then((confirmed) => {
      if (confirmed) {
        editSensor();
      }
    });
  }

  let niv = ['Mv1', 'Mv2', 'Mv3']

  const onChangeHandlerDate = (value, name, label) => {

    setDateSetting(state => ({ ...state, [`${name}_${label}`]: value }), [])
  }
  const onChangeHandlerMax = (value, name, label) => {
    setMaxSetting(state => ({ ...state, [`${name}_${label}`]: value }), [])
  }
  const onChangeHandlerMin = (value, name, label) => {
    setMinSetting(state => ({ ...state, [`${name}_${label}`]: value }), [])
  }
  const onChangeHandlerDepth = (value, index) => {
    setSensorDepths((state) => ({ ...state, [`mv${index + 1}`]: value }));
  }

  useEffect(() => {
    let dataArray = []
    dataArray.push({ date: dateSetting, min: minSetting, max: maxSetting })
    setSettingValues(dataArray)
  }, [dateSetting, minSetting, maxSetting])



  const Items = () => {
    let element = []

    for (let index = 0; index < niv.length; index++) {
      element.push(
        <tbody key={niv[index]}>
          <tr>
            <td>{niv[index]}</td>
            <td>
              <input
                name={'date'}
                className="form-control"
                type="date"
                required
                value={(dateSetting != null && typeof dateSetting[`Mv${index + 1}_date`] !== 'undefined') ? dateSetting[`Mv${index + 1}_date`] : ""}
                onChange={e => onChangeHandlerDate(e.target.value, niv[index], 'date')}
              />
            </td>
            <td>
              <input
                name={'min'}
                className="form-control"
                type="number"
                min="0"
                step="1"
                value={(minSetting != null && typeof minSetting[`Mv${index + 1}_min`] !== 'undefined') ? minSetting[`Mv${index + 1}_min`] : ""}
                onChange={e => onChangeHandlerMin(e.target.value, niv[index], "min")}

              />
            </td>
            <td>
              <input
                name={'max'}
                className="form-control"
                type="number"
                min="0"
                step="1"
                value={(maxSetting != null && typeof maxSetting[`Mv${index + 1}_max`] !== 'undefined') ? maxSetting[`Mv${index + 1}_max`] : ""}
                onChange={e => onChangeHandlerMax(e.target.value, niv[index], "max")}

              />
            </td>
            <td>
              <div className="sensor-depth-input-wrap">
                <input
                  name={`mv${index + 1}-depth`}
                  className="form-control sensor-depth-input"
                  type="number"
                  min="0"
                  max="1000"
                  step="1"
                  required
                  value={sensorDepths[`mv${index + 1}`]}
                  onChange={e => onChangeHandlerDepth(e.target.value, index)}
                  aria-label={`${t('depth_column')} ${niv[index]}`}
                />
                <span>cm</span>
              </div>
            </td>
          </tr>
        </tbody>

      )

    }
    return element
  }

  const formatHistoryDate = (value) => {
    if (!value) return t('now_label');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(i18n.language || 'fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCalibrationDate = (value) => {
    if (!value) return '—';
    return String(value).slice(0, 10).split('-').reverse().join('/');
  };

  const changeTypeLabel = (type) => ({
    initial_state: t('change_type_initial_state'),
    legacy_calibration: t('change_type_legacy_calibration'),
    depth: t('change_type_depth'),
    calibration: t('change_type_calibration'),
    depth_and_calibration: t('change_type_depth_and_calibration'),
  }[type] || type);



  return (
    <Container fluid className="main-content-container sensor-admin-page sensor-edit-page px-4 mt-3">
      <Link to='/admin/sensors' > {t("Go_Back")}</Link>

      {/* Page Header */}
      <Row noGutters className="page-header py-4 d-flex justify-content-between align-items-center">
        <PageTitle
          sm="4"
          title={t('edit_sensor')}
          subtitle={t('edit_sensor')}
          className="text-sm-left"
        />
        <div className="mt-4">
          <Button className="sensor-history-action" title="Historique" onClick={() => { navigate(`/my-history/${resultScan}`) }} variant="info"><i className="fas fa-clock-rotate-left me-2" aria-hidden="true" /> {t('View History')}</Button>

        </div>
      </Row>

      <Row>
        <Col lg="12" md="8" sm="12" className="mb-4">
          <Card small className="sensor-admin-card h-100">
            <Card.Header className="border-bottom">
              <div>
                <h6 className="m-0">{t('sensor_setup')}</h6>{" "}
              </div>
              <div className="sensor-admin-actions">
                <Button
                  onClick={confirmSensorSave}
                  className="mb-2 mr-1 btn btn-success"
                >
                  <i className="fas fa-check me-2" aria-hidden="true" />
                  {t('save')}
                </Button>
                <Button
                  className="mb-2 mr-1 btn btn-danger"
                  onClick={() => navigate('/admin/sensors')}
                >
                  <i className="fas fa-xmark me-2" aria-hidden="true" />
                  {t('cancel')}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="pt-0 px-1">
              <Row className="p-2 gap-2">
                <Col lg="7" sm="12" md="12">
                  <Form className="sensor-admin-form sensor-edit-form">
                     <Row form>
        <Col lg="6" md="12" sm="12" className="form-group">
          <FormControl sx={{ m: 1, width: "25ch" }} variant="outlined" size="small">
            <InputLabel htmlFor="outlined-adornment-sensorName">{t('sensor_Name')}</InputLabel>
            <OutlinedInput
              id="outlined-adornment-sensorName"
              type="text"
              placeholder={t('sensor_Name')}
              value={sensorName}
              onChange={e => setSensorName(e.target.value)}
              label={t('sensor_Name')}
            />
          </FormControl>
        </Col>

        <Col lg="6" md="12" sm="12" className="form-group">
          <FormControl sx={{ m: 1, width: "25ch" }} variant="outlined" size="small">
            <InputLabel htmlFor="outlined-adornment-password">{t('sensor_code')}</InputLabel>
            <OutlinedInput
              id="outlined-adornment-password"
              type="text"
              placeholder={t('sensor_code')}
              value={resultScan}
              onChange={e => setResultScan(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('toggle_qr_scanner')}
                    edge="end"
                    onClick={() => setShowScan(!showScan)}
                  >
                    <QrCodeScannerIcon />
                  </IconButton>
                </InputAdornment>
              }
              label={t('sensor_code')}
            />
          </FormControl>

          {showScan && (
            <div style={{ width: '250px', display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Html5QrcodePlugin
                fps={10}
                qrbox={250}
                disableFlip={true}
                rememberLastUsedCamera={false}
                qrCodeSuccessCallback={onNewScanResult}
              />
            </div>
          )}
        </Col>
      </Row>
      <Row form>
        <Col lg="6" md="12" sm="12" className="form-group">
          <FormControl sx={{ m: 1, width:"25ch" }} variant="outlined" size='small'>
            <InputLabel htmlFor="outlined-adornment-read-frequency">{t('sensor_read_frequency')}</InputLabel>
            <OutlinedInput
              id="outlined-adornment-read-frequency"
              type='number'
              inputProps={{ min: 60, max: 86400, step: 1 }}
              placeholder={t('sensor_read_frequency_placeholder')}
              value={frequency}
              onChange={e => { setFrequency(e.target.value) }}
              label={t('sensor_read_frequency')}
            />
          </FormControl>
        </Col>

        <Col lg="6" md="12" sm="12" className="form-group">
          <FormControl sx={{ m: 1, width:"25ch" }} variant="outlined" size='small'>
            <InputLabel htmlFor="outlined-adornment-send-frequency">{t('sensor_send_frequency')}</InputLabel>
            <OutlinedInput
              id="outlined-adornment-send-frequency"
              type='number'
              inputProps={{ min: 60, max: 259200, step: 1 }}
              placeholder={t('sensor_send_frequency_placeholder')}
              value={sendFrequency}
              onChange={e => { setSendFrequency(e.target.value) }}
              label={t('sensor_send_frequency')}
            />
          </FormControl>
        </Col>

        <Col lg="12" className="form-group">
          <fieldset className="sensor-firmware-fieldset" aria-describedby="sensor-firmware-help">
            <legend>{t('sensor_firmware_version')}</legend>
            <p id="sensor-firmware-help">{t('sensor_firmware_version_help')}</p>
            <div className="sensor-firmware-options">
              {[
                {
                  value: '1',
                  title: t('sensor_firmware_v1'),
                  description: t('sensor_firmware_v1_description'),
                  icon: 'fa-microchip',
                },
                {
                  value: '2',
                  title: t('sensor_firmware_v2'),
                  description: t('sensor_firmware_v2_description'),
                  icon: 'fa-layer-group',
                },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`sensor-firmware-option ${firmwareVersion === option.value ? 'is-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="firmwareVersion"
                    value={option.value}
                    checked={firmwareVersion === option.value}
                    onChange={(event) => setFirmwareVersion(event.target.value)}
                  />
                  <span className="sensor-firmware-option-icon" aria-hidden="true">
                    <i className={`fas ${option.icon}`} />
                  </span>
                  <span className="sensor-firmware-option-copy">
                    <strong>{option.title}</strong>
                    <small>{option.description}</small>
                  </span>
                  <span className="sensor-firmware-option-check" aria-hidden="true">
                    <i className="fas fa-check" />
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        </Col>
                    
                    
                      <Col lg="6" md="12" sm="12" className="form-group">
                        <FormControl sx={{ m: 1, width:"25ch" }} variant="outlined" size='small'>
                          <InputLabel htmlFor="outlined-adornment-password">{t('SIM card number')}</InputLabel>
                          <OutlinedInput
                            id="outlined-adornment-password"
                            type='text'
                            placeholder={t('SIM card number')}
                            value={simSettings.simNumber}
                            onChange={e => setSimSettings({ ...simSettings, simNumber: e.target.value })}
                            label={t('SIM card number')}
                          />
                        </FormControl>

                      </Col>
                      </Row>
                      <Row form>
                      <Col lg="6" md="12" sm="12" className="form-group">
                        <FormControl sx={{ m: 1, width:"25ch" }} variant="outlined" size='small'>
                          <InputLabel htmlFor="outlined-adornment-password">{t('SIM Card identifier')}</InputLabel>
                          <OutlinedInput
                            id="outlined-adornment-password"
                            type='text'
                            placeholder={t('SIM Card identifier')}
                            value={simSettings.simIdentify}
                            onChange={e => setSimSettings({ ...simSettings, simIdentify: e.target.value })}
                            label={t('SIM Card identifier')}
                          />
                        </FormControl>
                      </Col>
                   
                    
                      <Col lg="6" md="12" sm="12" className="form-group">
                        <FormControl sx={{ m: 1,width:"25ch" }} variant="outlined" size='small'>
                          <InputLabel htmlFor="outlined-adornment-password">{t('lat')}</InputLabel>
                          <OutlinedInput
                            id="outlined-adornment-password"
                            type='text'
                            placeholder={t('lat')}
                            value={sensorCoords.lat}
                            onChange={e => setSensorCoords({ ...sensorCoords, lat: e.target.value })}
                            label={t('lat')}
                          />
                        </FormControl>
                      </Col>
                      </Row>
                      <Row form>
                      <Col lg="6" md="12" sm="12" className="form-group">
                        <FormControl sx={{ m: 1,width:"25ch" }} variant="outlined" size='small'>
                          <InputLabel htmlFor="outlined-adornment-password">{t('lon')}</InputLabel>
                          <OutlinedInput
                            id="outlined-adornment-password"
                            type='text'
                            placeholder={t('lon')}
                            value={sensorCoords.lon}
                            onChange={e => setSensorCoords({ ...sensorCoords, lon: e.target.value })}
                            label={t('lon')}
                          />
                        </FormControl>
                      </Col>
                    </Row>
                   

                  </Form>
                </Col>
                  <Col lg="4" md="12" sm="12" className="mt-1" style={{ overflowX: "auto" }}>
                  <table className="table mb-0 border text-center  table-responsive-lg">
                        <thead className="bg-light">
                          <tr>

                            <th scope="col" className="border-0">{t('statu')}</th>
                            <th scope="col" className="border-0">{t('last_reading')}</th>
                            <th scope="col" className="border-0">{t('expected')}</th>
                          </tr>
                        </thead>
                        <tbody>

                          <tr>
                            <td style={{ color: sensorState === 'Active' ? 'green' : 'red', fontSize: 13, fontWeight: 'bold' }}>
                              {sensorState }
                            </td>
                            <td style={{ fontSize: 11.5, fontWeight: 'bold' }}>
                              {lastDataTime}
                            </td>
                            <td style={{ fontSize: 11.5, fontWeight: 'bold' }}>
                              {formattedTime}
                            </td>
                          </tr>



                        </tbody>

                      </table>
                      <div style={{ overflowX: "auto" }}>
                      <table className="table mb-0 border text-center mt-2  table-responsive-lg">
                        <thead className="bg-light">
                          <tr>

                            <th scope="col" className="border-0">{t('Timezone')}</th>
                            <th scope="col" className="border-0">{t('Location')}</th>
                            <th scope="col" className="border-0">{t('Link to map')}</th>
                          </tr>
                        </thead>
                        <tbody>

                          <tr>
                            <td>
                              GMT +{sensorCoords.locationInfo.offset}
                            </td>
                            <td>
                              {sensorCoords.locationInfo.timezone_id}
                            </td>
                            <td>
                              <a href={sensorCoords.locationInfo.map_url} target="_blank" rel="noopener noreferrer">{t('View position')}</a>
                            </td>
                          </tr>



                        </tbody>

                      </table>
                      </div>
                  </Col>

              </Row>
            </Card.Body>

            <Card.Body className="pt-0">
              <div
                style={{
                  display: "flex",
                  marginTop: "20px",
                  flexWrap: "wrap"
                }}
              >
                {
                  extranelToken != ""
                    ?
                    <Col lg="21" md="12" sm="12" className="mb-12">
                      <b>{t('link_to_get_data_sensor')} </b><br></br><br></br>
                      {`${process.env.REACT_APP_BASE_URL}/api-external-sensor/${extranelToken}/${resultScan}`}
                    </Col>
                    :
                    ''
                }
              </div>
            </Card.Body>
            <Row form className='mx-2 mb-2'>
                      <Col lg="12" md="12" sm="12" style={{ overflowX: "auto" }}>
                        <div className="sensor-calibration-heading">
                          <div>
                            <span className="sensor-section-kicker">{t('calibration_section_kicker')}</span>
                            <h3>{t('calibration_section_title')}</h3>
                            <p>{t('calibration_section_help')}</p>
                          </div>
                          <span className="sensor-traceability-badge"><i className="fas fa-shield-halved" aria-hidden="true" /> {t('traceability_active')}</span>
                        </div>
                        <table className="table mb-0 border text-center sensor-calibration-table table-responsive-lg">
                          <thead className="bg-light">
                            <tr>
                              <th scope="col" className="border-0">{t('niveau')}</th>
                              <th scope="col" className="border-0">{t('calibration_date')}</th>
                              <th scope="col" className="border-0">{t('min_raw_dry')}</th>
                              <th scope="col" className="border-0">{t('max_raw_wet')}</th>
                              <th scope="col" className="border-0">{t('depth_column')}</th>
                            </tr>
                          </thead>

                          {Items()}

                        </table>
                        <div className="sensor-calibration-save-action">
                          <div>
                            <strong>{t('pending_changes')}</strong>
                            <span>{t('pending_changes_help')}</span>
                          </div>
                          <Button
                            type="button"
                            variant="success"
                            onClick={confirmSensorSave}
                          >
                            <i className="fas fa-save me-2" aria-hidden="true" />
                            {t('save_depths_calibration')}
                          </Button>
                        </div>
                      
                      </Col>

                    </Row>
          </Card>
        </Col>
        
      </Row>
      <Row>
        <Col lg="12" className="mb-4">
          <Card className="sensor-admin-card sensor-config-history-card">
            <Card.Header className="border-bottom">
              <div>
                <span className="sensor-section-kicker">{t('config_history_kicker')}</span>
                <h6 className="m-0">{t('config_history_title')}</h6>
                <p className="mb-0">{t('config_history_help')}</p>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="sensor-config-history-filters">
                <label>
                  {t('niveau')}
                  <select
                    className="form-control"
                    value={historyFilters.level}
                    onChange={(event) => {
                      setHistoryPage(1);
                      setHistoryFilters((current) => ({ ...current, level: event.target.value }));
                    }}
                  >
                    <option value="">{t('all_levels')}</option>
                    <option value="1">Mv1</option>
                    <option value="2">Mv2</option>
                    <option value="3">Mv3</option>
                  </select>
                </label>
                <label>
                  {t('date_from')}
                  <input
                    className="form-control"
                    type="date"
                    value={historyFilters.from}
                    onChange={(event) => {
                      setHistoryPage(1);
                      setHistoryFilters((current) => ({ ...current, from: event.target.value }));
                    }}
                  />
                </label>
                <label>
                  {t('date_to')}
                  <input
                    className="form-control"
                    type="date"
                    value={historyFilters.to}
                    onChange={(event) => {
                      setHistoryPage(1);
                      setHistoryFilters((current) => ({ ...current, to: event.target.value }));
                    }}
                  />
                </label>
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="sensor-config-history-reset"
                  disabled={!historyFilters.level && !historyFilters.from && !historyFilters.to}
                  onClick={() => {
                    setHistoryPage(1);
                    setHistoryFilters({ level: '', from: '', to: '' });
                  }}
                >
                  {t('reset_filters')}
                </Button>
              </div>
              <div className="sensor-config-history-grid">
                {(historyFilters.level ? [Number(historyFilters.level)] : [1, 2, 3]).map((level) => {
                  const rows = configurationHistory.filter((entry) => Number(entry.level) === level);
                  return (
                    <section className="sensor-config-history-level" key={level}>
                      <header>
                        <span className={`sensor-level-dot sensor-level-dot-${level}`} />
                        <div>
                          <strong>{t('level_mv_label', { level })}</strong>
                          <small>{t('current_depth', { depth: sensorDepths[`mv${level}`] })}</small>
                        </div>
                      </header>
                      {rows.length ? (
                        <div className="sensor-config-history-scroll">
                          <table className="table sensor-config-history-table">
                            <thead>
                              <tr>
                                <th>{t('period_column')}</th>
                                <th>{t('depth_short')}</th>
                                <th>{t('min_max_column')}</th>
                                <th>{t('calibration_column')}</th>
                                <th>{t('modification_column')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((entry) => (
                                <tr key={entry.id}>
                                  <td>
                                    <strong>{formatHistoryDate(entry.effectiveFrom)}</strong>
                                    <small>→ {entry.isCurrent ? t('now_label') : formatHistoryDate(entry.effectiveTo)}</small>
                                  </td>
                                  <td>{entry.depth == null ? t('depth_not_available') : `${entry.depth} cm`}</td>
                                  <td>{entry.min ?? '—'} / {entry.max ?? '—'}</td>
                                  <td>{formatCalibrationDate(entry.calibrationDate)}</td>
                                  <td>
                                    <span className={`sensor-change-chip sensor-change-${entry.changeType}`}>
                                      {changeTypeLabel(entry.changeType)}
                                    </span>
                                    {(entry.changedByName || entry.changedByUid) && (
                                      <small>{t('changed_by', { name: entry.changedByName || entry.changedByUid })}</small>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="sensor-config-history-empty">{t('config_history_empty')}</div>
                      )}
                    </section>
                  );
                })}
              </div>
              <div className="sensor-config-history-pagination" aria-live="polite">
                <span>
                  {isHistoryLoading
                    ? t('history_loading')
                    : `${t('history_modifications_count', { count: historyPagination.total })} · ${t('history_page_of', { page: historyPagination.page, totalPages: historyPagination.totalPages })}`}
                </span>
                <div>
                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={isHistoryLoading || historyPagination.page <= 1}
                    onClick={() => setHistoryPage((current) => current - 1)}
                  >
                    <i className="fas fa-chevron-left me-1" aria-hidden="true" /> {t('previous')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={isHistoryLoading || historyPagination.page >= historyPagination.totalPages}
                    onClick={() => setHistoryPage((current) => current + 1)}
                  >
                    {t('next')} <i className="fas fa-chevron-right ms-1" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default EditSensorByAdmin
